import { isEqual, set } from 'lodash';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '../../RootState';

import type { ErrorRecord } from './state';
import { initialVersionState } from './state';

const formSlice = createSlice({
  name: 'form',
  initialState: initialVersionState,
  reducers: {
    setVersion: (state, action: PayloadAction<number>) => {
      state.currentVersion = action.payload;
    },
    initForm: (
      state,
      action: PayloadAction<{
        draftForm: Record<string, unknown>;
        meta: { version: number | undefined; lastVersion: number; wasLive: boolean | undefined };
      }>
    ) => {
      state.currentVersion = action.payload.meta.version;
      state.lastVersion = action.payload.meta.lastVersion;
      state.draftForm = action.payload?.draftForm;
      state.initialForm = action.payload?.draftForm;
      state.wasLive = action.payload.meta.wasLive;

      state.errors = {};
    },

    updateField: (state, action: PayloadAction<{ field: string; value: unknown }>) => {
      if (state.draftForm) {
        set(state.draftForm, action.payload.field, action.payload.value);
      }
    },
    reset: (state) => {
      state.draftForm = null;
      state.initialForm = null;
      state.lastVersion = undefined;
      state.currentVersion = undefined;
      state.errors = {};
    },
    setErrors: (state, action: PayloadAction<ErrorRecord>) => {
      state.errors = action.payload;
    },
    resetChanges: (state) => {
      if (state.initialForm) {
        state.draftForm = state.initialForm;
        state.errors = {};
      }
    },
    openDialog: (
      state,
      action: PayloadAction<{
        mode: 'create' | 'copy' | 'view';
        formData: Record<string, unknown>;
        title?: string;
      }>
    ) => {
      const { mode, formData, title } = action.payload;

      state.ui.isDialogOpen = true;
      state.ui.dialogMode = mode;
      state.ui.dialogTitle = title || null;

      state.draftForm = formData;
      state.initialForm = formData;

      state.errors = {};
    },

    closeDialog: (state) => {
      state.ui.isDialogOpen = false;
      state.ui.dialogMode = null;
      state.ui.dialogTitle = null;
    },
  },
});

const base = {
  state: (state: RootState) => state.versions,
  currentVersion: (state: RootState) => state.versions.currentVersion,
  lastVersion: (state: RootState) => state.versions.lastVersion,
  draftForm: (state: RootState) => state.versions.draftForm,
  initialForm: (state: RootState) => state.versions.initialForm,
  errors: (state: RootState) => state.versions.errors,
  selectDialogUI: (state: RootState) => state.versions.ui,
  wasLive: (state: RootState) => state.versions.wasLive,
};

const noChanges = createSelector([base.draftForm, base.initialForm], (draft, initialForm) => {
  if (!draft || !initialForm) return true;
  return isEqual(draft, initialForm);
});
export const versionToolkit = {
  initialState: initialVersionState,
  actions: formSlice.actions,
  selectors: {
    ...base,
    noChanges,
  },
};

export const versionReducer = formSlice.reducer;
